import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ServiceByCategory } from "@/types/services";
import { getServiceStatus } from "./serviceStatus";
import type { ServiceColumnKey } from "./serviceColumns";

interface ServiceTableRowProps {
  service: ServiceByCategory;
  categoryName: string;
  visibleColumns: Set<ServiceColumnKey>;
  onView: () => void;
}

const Cell = ({
  show,
  className,
  children,
}: {
  show: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  if (!show) return null;
  return (
    <TableCell className={cn("align-middle", className)}>{children}</TableCell>
  );
};

export const ServiceTableRow = ({
  service,
  categoryName,
  visibleColumns,
  onView,
}: ServiceTableRowProps) => {
  const status = getServiceStatus(service);
  const mode = service.mode ?? service.mode_of_service ?? "—";

  return (
    <TableRow className="border-b border-border/60 transition-colors hover:bg-muted/40">
      {/* Service Name + image + wrapped description (always on) */}
      <TableCell className="w-[360px] max-w-[360px] align-middle">
        <div className="flex min-w-0 items-start gap-3 py-1">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted text-xs font-medium text-muted-foreground">
            {service.image ? (
              <img
                src={service.image}
                alt={service.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span>{service.name?.[0]?.toUpperCase() ?? "S"}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="z-body-md text-foreground">{service.name}</p>
            {service.description ? (
              <p className="z-secondary line-clamp-2 text-muted-foreground">
                {service.description}
              </p>
            ) : null}
          </div>
        </div>
      </TableCell>

      <Cell show={visibleColumns.has("mode")}>
        <Badge variant="secondary" className="capitalize">
          {mode}
        </Badge>
      </Cell>

      <Cell
        show={visibleColumns.has("duration")}
        className="z-body-md text-muted-foreground"
      >
        {service.event_duration ?? "—"} mins
      </Cell>

      <Cell show={visibleColumns.has("department")}>
        <Badge variant="outline">{categoryName}</Badge>
      </Cell>

      <Cell show={visibleColumns.has("status")}>
        <Badge variant={status.variant}>{status.label}</Badge>
      </Cell>

      {/* Optional columns */}
      <Cell
        show={visibleColumns.has("price")}
        className="z-body-md text-foreground"
      >
        ₹{service.cost ?? "0"}
      </Cell>

      <Cell
        show={visibleColumns.has("slot_duration")}
        className="z-body-md text-muted-foreground"
      >
        {service.slot_duration ?? "—"} mins
      </Cell>

      <Cell
        show={visibleColumns.has("start_buffer")}
        className="z-body-md text-muted-foreground"
      >
        {service.start_buffer ?? 0} mins
      </Cell>

      <Cell
        show={visibleColumns.has("end_buffer")}
        className="z-body-md text-muted-foreground"
      >
        {service.end_buffer ?? 0} mins
      </Cell>

      <Cell
        show={visibleColumns.has("min_time_to_start")}
        className="z-body-md text-muted-foreground"
      >
        {service.min_time_to_start ?? 0} mins
      </Cell>

      <Cell
        show={visibleColumns.has("order")}
        className="z-body-md text-muted-foreground tabular-nums"
      >
        {service.order ?? 0}
      </Cell>

      <Cell show={visibleColumns.has("followup")}>
        {service.is_followup_available ? (
          <Badge variant="success">
            {service.followup_limit_days
              ? `${service.followup_limit_days}-day`
              : "Available"}
          </Badge>
        ) : (
          <Badge variant="neutral">Off</Badge>
        )}
      </Cell>

      <Cell show={visibleColumns.has("type")}>
        <Badge variant="outline" className="capitalize">
          {service.type ?? "—"}
        </Badge>
      </Cell>

      {/* Actions */}
      <TableCell className="text-right">
        <Button
          id={`view-service-btn-${service.id}`}
          variant="outline"
          size="sm"
          onClick={onView}
        >
          <Eye className="mr-1.5 h-3.5 w-3.5" />
          View
        </Button>
      </TableCell>
    </TableRow>
  );
};
